/**
 * The first-list wait — PRD §2, §5.
 *
 * Two rules shape everything here:
 *
 *  1. "Do not display a fake percentage or promise an exact completion time."
 *     There is no progress bar and no ETA. The panel shows that work is
 *     continuing, and completion is signalled by the actual list-ready event.
 *  2. "Questions save as answered and never become prerequisites for the
 *     list." Every question is skippable, answers post as they are given, and
 *     the list-ready action takes priority over the question in view — §2
 *     requires an immediate route to the list if it arrives mid-question, with
 *     any answer already given retained.
 */

import React, { useState } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { copyFor, SHARED } from './audienceCopy';

function Question({ question, onAnswer, onSkip, index, total }) {
  const [selected, setSelected] = useState([]);
  const [otherText, setOtherText] = useState('');
  const showOther = selected.includes('Other');

  const toggle = (choice) => {
    setSelected((prev) => (
      prev.includes(choice) ? prev.filter((c) => c !== choice) : [...prev, choice]
    ));
  };

  return (
    <section className="cmp-question">
      <p className="cmp-progress">Question {index + 1} of {total} · optional</p>
      <h2 className="cmp-section-title">{question.question}</h2>
      <p className="cmp-help">Choose any that fit, or skip.</p>

      <div className="cmp-choices">
        {question.choices.map((choice) => (
          <label className="cmp-choice" key={choice}>
            {/* §5 — "use conventional checkboxes for choices". Multi-select,
                so a real checkbox rather than a pill that a screen reader
                would announce as a radio. */}
            <input
              type="checkbox"
              checked={selected.includes(choice)}
              onChange={() => toggle(choice)}
            />
            <span>{choice}</span>
          </label>
        ))}
      </div>

      {/* §5 — "Other" reveals an optional short field. */}
      {showOther && (
        <div className="cmp-field">
          <input
            className="cmp-input"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder={SHARED.otherFieldLabel}
            aria-label={SHARED.otherFieldLabel}
          />
        </div>
      )}

      <div className="cmp-actions">
        <button type="button" className="cmp-btn cmp-btn--quiet" onClick={onSkip}>
          {SHARED.skip}
        </button>
        <button
          type="button"
          className="cmp-btn cmp-btn--secondary"
          onClick={() => onAnswer({
            key: question.key,
            question: question.question,
            choices: selected,
            otherText: showOther ? otherText : '',
          })}
          disabled={selected.length === 0}
        >
          {SHARED.next}
        </button>
      </div>
    </section>
  );
}

export default function WaitingStep({
  audience, brief, state, questionIndex, onAnswer, onSkip, onOpenList, onRetry, retrying,
  attemptsRemaining = 2, scope = null,
}) {
  const copy = copyFor(audience);
  const questions = copy.questions;
  const question = questions[questionIndex] || null;
  const ready = state === 'ready';
  const empty = state === 'empty';
  // Q3 — two different failures with two different screens. `delayed` means
  // the run is past five minutes but alive; `failed` means it stopped. Both
  // offer Try again, until the attempts run out.
  const delayed = state === 'delayed';
  const failed = state === 'failed';
  const exhausted = attemptsRemaining <= 0;
  const stalled = delayed || failed;
  // Only the working state reserves height. Every settled state sizes to its
  // own content, which is what stops the panel reading as a half-empty box.
  const busy = !ready && !empty && !stalled;

  return (
    <>
      <p className="cmp-eyebrow">Your first list</p>
      {/* §2 — a truthful state, heading included. "We’re finding stories for
          your coverage" stops being true the moment the run concludes, in
          either direction. */}
      {/* Three different stops, three different sentences. A run that FAILED
          is not "taking longer than expected" — that is the approved copy for
          a run still going past five minutes, and using it for a hard failure
          would be the same untruth as a spinner over a finished job. */}
      <h1 className="cmp-title">
        {empty
          ? copy.firstListEmptyTitle
          : exhausted
            ? SHARED.listFailedFinalTitle
            : delayed
              ? SHARED.listDelayed
              : failed
                ? SHARED.firstListFailedTitle
                : copy.firstListWait}
      </h1>
      {!empty && !stalled && <p className="cmp-lede">{copy.firstListWaitSupport}</p>}

      <div className="cmp-split">
        <div>
          {/* §2 — "make the entered brief accessible". A visitor who has waited
              two minutes should not have to remember what they asked for. */}
          <div className="cmp-brief-echo">
            <strong>Your brief</strong>
            {brief}
          </div>

          {stalled && (
            <>
              {/* Q3 — after three attempts there is no fourth. The approved
                  copy says so and stops offering a button that will refuse. */}
              {/* `delayed` needs no notice: the heading is the whole message
                  Or approved for it, and repeating it underneath would say
                  the same thing twice. */}
              {exhausted ? (
                <div className="cmp-notice">{SHARED.listFailedFinalBody}</div>
              ) : failed ? (
                <div className="cmp-notice">{SHARED.firstListFailed}</div>
              ) : null}
              {!exhausted && (
                <div className="cmp-actions">
                  <button
                    type="button"
                    className="cmp-btn cmp-btn--primary"
                    onClick={onRetry}
                    disabled={retrying}
                  >
                    {SHARED.tryAgain}
                  </button>
                </div>
              )}
            </>
          )}

          {empty && <div className="cmp-notice">{SHARED.firstListEmpty}</div>}

          {/* The questions keep running while the list builds, and stop the
              moment there is something better to do. */}
          {!stalled && !empty && question && (
            <Question
              key={question.key}
              question={question}
              index={questionIndex}
              total={questions.length}
              onAnswer={onAnswer}
              onSkip={onSkip}
            />
          )}

          {!stalled && !empty && !question && !ready && (
            <p className="cmp-help">Thanks — nothing else is needed from you.</p>
          )}
        </div>

        {/* The panel. Flat navy per the brand book; the rings are the mockup's
            motif, which the PRD asks to keep. */}
        <div className={`cmp-panel${busy ? ' cmp-panel--busy' : ''}`}>
          <div className="cmp-panel-content">
            {empty || stalled ? (
              <>
                <h2 className="cmp-panel-title">
                  {empty ? 'Nothing to show' : exhausted ? 'Stopped' : 'Still working'}
                </h2>
                {/* Only the empty case gets a second line. The failure is
                    already explained beside the retry button; repeating it
                    here would say the same thing twice on one screen. */}
                {empty && <p className="cmp-panel-sub">{SHARED.firstListEmptySub}</p>}
              </>
            ) : ready ? (
              <>
                {/* §2 — the list-ready action takes priority over whatever
                    question is on screen. It does not interrupt an answer in
                    progress; it offers an immediate route, and anything
                    already answered has already been saved. */}
                <div className="cmp-panel-mark" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="20" height="20">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <h2 className="cmp-panel-title">{copy.firstListReady}</h2>
                <p className="cmp-panel-sub">Explore it now.</p>
                <button type="button" className="cmp-btn cmp-btn--primary" onClick={onOpenList}>
                  {SHARED.openList}
                  <ArrowForwardIcon style={{ fontSize: 16 }} />
                </button>
              </>
            ) : (
              <>
                {/* The rings are an ACTIVITY indicator: they exist only while
                    there is activity. Past five minutes, or once the run has
                    settled, this whole branch is gone — leaving them turning
                    is the same untruth as leaving the heading up (Q3). */}
                <div className="cmp-rings" aria-hidden="true">
                  <span className="cmp-ring cmp-ring--1" />
                  <span className="cmp-ring cmp-ring--2" />
                  <span className="cmp-ring cmp-ring--3" />
                  <span className="cmp-ring cmp-ring--core" />
                  <span className="cmp-ring cmp-ring--arc" />
                </div>
                {/* Truthful, and no more: no percentage, no ETA. */}
                <h2 className="cmp-panel-title">Working on it</h2>
                <p className="cmp-panel-sub">
                  Searching across countries, languages and local sources.
                </p>
                {/* The scope of the search, from the run's own configuration.
                    Constants, not counters — see `scope` in campaignService. */}
                {scope && (scope.keywords > 0 || scope.categories > 0) && (
                  <div className="cmp-readout">
                    <div className="cmp-readout-item">
                      <span className="cmp-readout-figure">{scope.hours}h</span>
                      <span className="cmp-readout-label">Window</span>
                    </div>
                    {scope.keywords > 0 && (
                      <div className="cmp-readout-item">
                        <span className="cmp-readout-figure">{scope.keywords}</span>
                        <span className="cmp-readout-label">
                          {scope.keywords === 1 ? 'Keyword' : 'Keywords'}
                        </span>
                      </div>
                    )}
                    {scope.categories > 0 && (
                      <div className="cmp-readout-item">
                        <span className="cmp-readout-figure">{scope.categories}</span>
                        <span className="cmp-readout-label">
                          {scope.categories === 1 ? 'Category' : 'Categories'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
